import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, money, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AuctionCard from "@/components/AuctionCard";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Package, Gavel, Trophy, Heart, Sparkles, Zap } from "lucide-react";

export default function DashboardPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [bids, setBids] = useState([]);
  const [won, setWon] = useState([]);
  const [selling, setSelling] = useState([]);
  const [watchlist, setWatchlist] = useState([]);
  const [boostingId, setBoostingId] = useState(null);

  const boostListing = async (auctionId) => {
    setBoostingId(auctionId);
    try {
      const { data } = await api.post("/boost/checkout", {
        auction_id: auctionId,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatError(e));
      setBoostingId(null);
    }
  };

  useEffect(() => {
    if (!user || typeof user !== "object") return;
    Promise.all([
      api.get("/auctions/mine/bids").then((r) => setBids(r.data)).catch(() => {}),
      api.get("/auctions/mine/won").then((r) => setWon(r.data)).catch(() => {}),
      api.get("/auctions/mine/selling").then((r) => setSelling(r.data)).catch(() => {}),
      api.get("/watchlist").then((r) => setWatchlist(r.data)).catch(() => {}),
    ]);
  }, [user]);

  if (!user || typeof user !== "object") {
    return <div className="max-w-7xl mx-auto p-8 text-zinc-500">Loading…</div>;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="mb-10">
        <div className="text-[10px] tracking-[0.3em] uppercase text-orange-500 mb-2">Dashboard</div>
        <h1 className="font-display text-4xl sm:text-5xl font-black uppercase tracking-tighter">
          Welcome, {user.name || user.email.split("@")[0]}
        </h1>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10">
        <StatCard icon={<Gavel />} label="Active Bids" value={bids.filter((b) => b.status === "active").length} testid="stat-bids" />
        <StatCard icon={<Trophy />} label="Won" value={won.length} testid="stat-won" />
        <StatCard icon={<Package />} label="Listings" value={selling.length} testid="stat-listings" />
        <StatCard icon={<Heart />} label="Watching" value={watchlist.length} testid="stat-watchlist" />
      </div>

      <Tabs defaultValue="bids" className="w-full">
        <TabsList className="rounded-none bg-zinc-950 border border-zinc-800 p-1 h-auto">
          <TabsTrigger data-testid="tab-bids" value="bids" className="rounded-none text-xs uppercase tracking-widest data-[state=active]:bg-orange-600 data-[state=active]:text-white">My Bids</TabsTrigger>
          <TabsTrigger data-testid="tab-won" value="won" className="rounded-none text-xs uppercase tracking-widest data-[state=active]:bg-orange-600 data-[state=active]:text-white">Won</TabsTrigger>
          <TabsTrigger data-testid="tab-watchlist" value="watchlist" className="rounded-none text-xs uppercase tracking-widest data-[state=active]:bg-orange-600 data-[state=active]:text-white">Watchlist</TabsTrigger>
          <TabsTrigger data-testid="tab-selling" value="selling" className="rounded-none text-xs uppercase tracking-widest data-[state=active]:bg-orange-600 data-[state=active]:text-white">Selling</TabsTrigger>
        </TabsList>

        <TabsContent value="bids" className="mt-6">
          <ListGrid items={bids} empty="You haven't placed any bids yet." />
        </TabsContent>
        <TabsContent value="won" className="mt-6">
          <ListGrid items={won} empty="No wins yet — keep bidding." />
        </TabsContent>
        <TabsContent value="watchlist" className="mt-6">
          <ListGrid items={watchlist} empty="Your watchlist is empty." />
        </TabsContent>
        <TabsContent value="selling" className="mt-6">
          {selling.length === 0 ? (
            <EmptyState message="You have no listings yet." cta={<Link to="/sell" className="text-orange-500 hover:underline">Create your first listing →</Link>} />
          ) : (
            <div className="space-y-3">
              {selling.map((a, i) => (
                <div key={a.id} className="bg-zinc-950 border border-zinc-800 hover:border-zinc-700 transition-colors p-4 grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-4 items-center">
                  <Link to={`/auctions/${a.id}`} className="flex items-center gap-4 min-w-0">
                    <div className="w-16 h-16 shrink-0 bg-black border border-zinc-800 overflow-hidden">
                      <img src={a.hero_image || a.photos?.[0] || "https://images.unsplash.com/photo-1649313444539-a8900c5cdc54?w=200&auto=format&fit=crop&q=80"} alt="" className="w-full h-full object-cover" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-display font-bold uppercase tracking-tight text-white truncate">{a.title}</span>
                        {a.is_featured && (
                          <span className="inline-flex items-center gap-1 bg-orange-500 text-white px-1.5 py-0.5 text-[9px] font-semibold tracking-widest uppercase">
                            <Sparkles className="w-2.5 h-2.5" /> Featured
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-xs text-zinc-500">
                        <span>{a.city}, {a.state}</span>
                        <span>·</span>
                        <span className="uppercase tracking-widest text-[10px]">{a.status}</span>
                        <span>·</span>
                        <span className="text-orange-500 font-semibold">{money(a.current_bid || a.starting_bid)} · {a.bid_count} bids</span>
                      </div>
                    </div>
                  </Link>
                  <div className="text-right">
                    {a.status === "active" && !a.is_featured && (
                      <Button
                        onClick={() => boostListing(a.id)}
                        disabled={boostingId === a.id}
                        data-testid={`boost-listing-${a.id}`}
                        variant="outline"
                        size="sm"
                        className="rounded-none bg-transparent border-orange-500/50 hover:border-orange-500 hover:bg-orange-500/10 text-orange-500 hover:text-orange-400 h-9 text-xs uppercase tracking-widest"
                      >
                        <Zap className="w-3 h-3 mr-1.5" />
                        {boostingId === a.id ? "…" : "Boost $15"}
                      </Button>
                    )}
                    {a.is_featured && (
                      <span className="text-[10px] tracking-widest uppercase text-orange-500 font-semibold">Featured</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function StatCard({ icon, label, value, testid }) {
  return (
    <div data-testid={testid} className="bg-zinc-950 border border-zinc-800 p-4">
      <div className="text-orange-500">{icon}</div>
      <div className="font-display text-3xl font-black text-white mt-3 tabular-nums">{value}</div>
      <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 mt-1">{label}</div>
    </div>
  );
}

function ListGrid({ items, empty }) {
  if (!items || items.length === 0) return <EmptyState message={empty} />;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
      {items.map((a, i) => <AuctionCard key={a.id} auction={a} index={i} />)}
    </div>
  );
}

function EmptyState({ message, cta }) {
  return (
    <div className="text-center py-16 border border-dashed border-zinc-800">
      <div className="font-display text-2xl uppercase tracking-tight text-zinc-500">{message}</div>
      {cta && <div className="mt-4">{cta}</div>}
    </div>
  );
}
